/**
 * Frota · Seguro de Carga.
 *
 * Quanto o seguro custa no período, quanto pesa em cada cliente, se a fatura da
 * corretora bate com o esperado, quando as apólices vencem e os sinistros. A
 * conferência das exigências de cada viagem é do cliente e não passa por aqui.
 */
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { CargoInsuranceOverviewDto } from "@bi/types";
import { api } from "../../lib/api";
import { fmtBRL, fmtNumber } from "../../lib/format";
import { useFilters, periodToDates } from "../../lib/filters";
import { useAuth } from "../../lib/auth";
import { moneyShort, pctShort, dateSp, claimNetLoss, CLAIM_STATUS } from "../../lib/cargo-insurance";
import { PageHeader } from "../../components/PageHeader";
import { KpiTile } from "../../components/intelligence/KpiTile";
import { QueryErrorBanner } from "../../components/QueryErrorBanner";
import { IconAlertTriangle, IconInfo, IconPlus } from "../../components/icons";
import { ClaimDialog } from "./dialogs";

export function CargoInsurancePage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "gestor";
  const [claimOpen, setClaimOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["cargo-insurance", "overview", from, to],
    queryFn: () => api.get<CargoInsuranceOverviewDto>(`/cargo-insurance/overview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const k = data?.kpis;
  const p = data?.premium;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Seguro de Carga"
        subtitle="Quanto o seguro custa, quanto pesa em cada cliente e quando as apólices vencem."
      />

      {isError && <QueryErrorBanner onRetry={() => { void refetch(); }} />}

      {data?.alerts.map((a, i) => (
        <div
          key={i}
          role={a.severity === "critical" ? "alert" : undefined}
          className={`flex gap-3 rounded-xl border px-4 py-3 ${a.severity === "critical" ? "bg-danger-50 border-danger-100" : a.severity === "warning" ? "bg-warning-50 border-warning-100" : "bg-surface-muted border-surface-border"}`}
        >
          {a.severity === "info"
            ? <IconInfo className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
            : <IconAlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${a.severity === "critical" ? "text-danger-600" : "text-warning-600"}`} />}
          <div>
            <p className={`text-sm font-semibold ${a.severity === "critical" ? "text-danger-700" : a.severity === "warning" ? "text-warning-700" : "text-gray-800"}`}>{a.title}</p>
            <p className="text-sm text-gray-700 mt-0.5">{a.text}</p>
          </div>
        </div>
      ))}

      <section aria-label="Indicadores" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiTile loading={isLoading} label="Custo esperado do seguro" value={k ? fmtBRL(k.expectedPremium) : "—"}
          footnote="Taxa das apólices sobre a mercadoria, prêmio mínimo, IOF e RC-V" />
        <KpiTile loading={isLoading} label="Custo sobre a mercadoria" value={k ? pctShort(k.costPctOfGoods) : "—"}
          footnote={k ? `${moneyShort(k.goodsTotal)} transportados` : null} />
        <KpiTile loading={isLoading} label="Custo por viagem" value={k?.costPerTrip != null ? fmtBRL(k.costPerTrip) : "—"}
          footnote={k ? `${fmtNumber(k.trips)} viagens no período` : null} />
        <KpiTile loading={isLoading} label="Custo sobre a receita de frete" value={k ? pctShort(k.costPctOfRevenue) : "—"}
          footnote="Quanto da receita bruta dos CT-e vai para o seguro" />
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6 items-start">
        {p && (
          <section aria-label="Fatura" className="card p-5 space-y-3 min-w-0">
            <h3 className="font-display text-lg font-semibold text-gray-900">Esperado × lançado</h3>
            <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-sm">
              {p.lines.map((l) => (
                <div key={l.label} className="contents">
                  <dt className="text-gray-600">{l.label}{l.note ? <span className="block text-[11px] text-gray-500">{l.note}</span> : null}</dt>
                  <dd className="font-medium tabular-nums text-gray-900">{fmtBRL(l.total)}</dd>
                </div>
              ))}
              <dt className="text-gray-600 border-t border-surface-border pt-1.5">Seguro de carga lançado no financeiro</dt>
              <dd className="font-medium tabular-nums border-t border-surface-border pt-1.5">{p.hasPaid ? fmtBRL(p.paidCargo) : "sem lançamento"}</dd>
              {p.diff != null && (
                <>
                  <dt className={p.diff > 0 ? "font-semibold text-danger-700" : "text-gray-600"}>Diferença (RCTR-C + RC-DC)</dt>
                  <dd className={`tabular-nums ${p.diff > 0 ? "font-semibold text-danger-700" : "text-gray-900"}`}>{fmtBRL(p.diff)}</dd>
                </>
              )}
            </dl>
            {p.impliedMissingGoods != null && (
              <p className="text-xs text-gray-700 leading-snug">
                Lançado a menos que o esperado: cerca de <strong>{moneyShort(p.impliedMissingGoods)}</strong> de mercadoria podem não ter sido averbados.
                A fatura costuma ser paga no mês seguinte, então compare meses fechados.
              </p>
            )}
          </section>
        )}

        <section aria-label="Custo por cliente" className="card p-5 space-y-3 min-w-0">
          <div>
            <h3 className="font-display text-lg font-semibold text-gray-900">Quanto o seguro pesa em cada cliente</h3>
            <p className="text-xs text-gray-600">Estimativa pela taxa vigente em cada viagem, sem o prêmio mínimo.</p>
          </div>
          {data && data.customers.length === 0 && <p className="text-sm text-gray-600">Sem CT-e no período.</p>}
          {data && data.customers.length > 0 && (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-1 py-1.5 font-semibold">Cliente</th>
                    <th className="px-1 py-1.5 font-semibold text-right">Mercadoria</th>
                    <th className="px-1 py-1.5 font-semibold text-right">Custo</th>
                    <th className="px-1 py-1.5 font-semibold text-right">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {data.customers.slice(0, 15).map((c) => (
                    <tr key={c.customerKey}>
                      <td className="px-1 py-1.5 text-gray-900 max-w-[16rem] truncate">{c.label}</td>
                      <td className="px-1 py-1.5 text-right tabular-nums text-gray-700">{moneyShort(c.goodsValue)}</td>
                      <td className="px-1 py-1.5 text-right tabular-nums font-medium text-gray-900">{fmtBRL(c.cost)}</td>
                      <td className="px-1 py-1.5 text-right tabular-nums text-gray-500">{pctShort(c.costPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.customers.length > 15 && <p className="text-xs text-gray-500 mt-2 px-1">e mais {data.customers.length - 15} clientes no período.</p>}
            </div>
          )}
        </section>
      </div>

      {data && (
        <section aria-label="Sinistros" className="card p-5 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-lg font-semibold text-gray-900">Sinistros</h3>
            {canEdit && <button type="button" className="btn-ghost text-xs" onClick={() => setClaimOpen(true)}><IconPlus className="w-3.5 h-3.5" /> Registrar sinistro</button>}
          </div>
          {data.claims.length === 0 ? (
            <p className="text-sm text-gray-600">Nenhum sinistro registrado.</p>
          ) : (
            <ul className="divide-y divide-surface-border text-sm">
              {data.claims.slice(0, 10).map((c) => {
                const net = claimNetLoss(c);
                return (
                  <li key={c.id} className="py-2 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5">
                    <span className="text-gray-900 truncate">{dateSp(c.occurredAt)} · {c.description}{c.plate ? ` · ${c.plate}` : ""}</span>
                    <span className="text-gray-600 whitespace-nowrap text-xs self-center">{CLAIM_STATUS[c.status]}</span>
                    <span className="text-xs text-gray-600">
                      {c.lossValue != null ? `Prejuízo ${fmtBRL(c.lossValue)}` : "Prejuízo não informado"}
                      {c.indemnityValue != null ? ` · indenizado ${fmtBRL(c.indemnityValue)}` : ""}
                    </span>
                    {net != null && <span className={`text-xs tabular-nums text-right ${net > 0 ? "font-semibold text-danger-700" : "text-gray-600"}`}>{net > 0 ? `ficou ${fmtBRL(net)}` : "sem perda"}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {data && (
        <footer className="text-xs text-gray-500 leading-relaxed space-y-1">
          <p>Viagem = CT-es do mesmo caminhão na mesma viagem do ERP (sem viagem, mesmo caminhão no mesmo dia). O custo usa a apólice vigente na data de cada viagem.</p>
          <p>Fontes: CT-e, viagens e contas de seguro (Sinersoft). Sinistros registrados nesta tela.</p>
        </footer>
      )}

      {claimOpen && <ClaimDialog onClose={() => setClaimOpen(false)} />}
    </div>
  );
}
